import { useRef } from "react";
import { Icon } from "@common/index";
import { useDialogFocusTrap } from "@/hooks/useDialogFocusTrap";
import type { LocationResponse } from "@models/location";

interface LocationSelectModalProps {
    locations: LocationResponse[];
    selectedLocationId?: string;
    onSelect: (locationId: string) => void;
    onClose: () => void;
}

export const LocationSelectModal = ({
    locations,
    selectedLocationId,
    onSelect,
    onClose,
}: LocationSelectModalProps) => {
    const panelRef = useRef<HTMLDivElement>(null);

    // The backdrop here is its own full-bleed Close button (below), so the
    // hook's backdrop handlers are not used.
    const { panelProps } = useDialogFocusTrap({
        panelRef,
        isProcessing: false,
        onRequestClose: onClose,
        focusPanelOnOpen: true,
    });

    const handleSelect = (locationId: string) => {
        onSelect(locationId);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <button
                type="button"
                aria-label="Close"
                className="absolute inset-0 bg-black/40"
                onClick={onClose}
            />
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-label="Select shipping location"
                {...panelProps}
                className="relative bg-base-100 rounded-lg w-full max-w-md max-h-[80vh] overflow-auto shadow-xl focus:outline-none"
            >
                <div className="flex items-center justify-between p-4 border-b border-base-300">
                    <h2 className="font-bold text-lg text-neutral">
                        Select shipping location
                    </h2>
                    <button
                        type="button"
                        aria-label="Close"
                        onClick={onClose}
                        className="btn btn-ghost btn-sm btn-square text-neutral"
                    >
                        <Icon name="close" className="text-xl" />
                    </button>
                </div>
                <ul className="p-2">
                    {locations.length === 0 ? (
                        <li className="p-4 text-sm text-neutral/70">
                            No shipping locations available.
                        </li>
                    ) : (
                        locations.map((location) => {
                            const isSelected =
                                location.id === selectedLocationId;
                            return (
                                <li key={location.id}>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            handleSelect(location.id)
                                        }
                                        aria-pressed={isSelected}
                                        className={`w-full text-left rounded-md p-3 flex items-start gap-3 hover:bg-base-200 ${
                                            isSelected ? "bg-base-200" : ""
                                        }`}
                                    >
                                        <Icon
                                            name={
                                                isSelected
                                                    ? "radio_button_checked"
                                                    : "radio_button_unchecked"
                                            }
                                            className={`text-xl mt-0.5 ${
                                                isSelected
                                                    ? "text-primary"
                                                    : "text-neutral/40"
                                            }`}
                                        />
                                        <span className="min-w-0">
                                            <span className="block font-semibold text-neutral">
                                                {location.name}
                                            </span>
                                            <span className="block text-sm text-neutral/70">
                                                {location.streetAddress}
                                            </span>
                                            <span className="block text-sm text-neutral/70">
                                                {location.city},{" "}
                                                {location.state}{" "}
                                                {location.zipCode}
                                            </span>
                                        </span>
                                    </button>
                                </li>
                            );
                        })
                    )}
                </ul>
            </div>
        </div>
    );
};
